<template>
  <clinic-layout title="Dashboard" :subtitle="todayLabel">
    <div class="adm-stats">
      <div class="adm-card adm-stat clinic-dash__stat">
        <span class="adm-stat__icon adm-tone-blue"><i class="fas fa-user-md"></i></span>
        <span>
          <span class="adm-stat__label">Doctors</span>
          <span class="adm-stat__value">
            <span v-if="loading" class="adm-skeleton clinic-dash__skeleton"></span>
            <template v-else>{{ doctors.length }}</template>
          </span>
        </span>
      </div>
      <div class="adm-card adm-stat clinic-dash__stat">
        <span class="adm-stat__icon adm-tone-green"><i class="fas fa-calendar-day"></i></span>
        <span>
          <span class="adm-stat__label">Appointments today</span>
          <span class="adm-stat__value">
            <span v-if="!statsLoaded" class="adm-skeleton clinic-dash__skeleton"></span>
            <template v-else>{{ totals.today }}</template>
          </span>
        </span>
      </div>
      <div class="adm-card adm-stat clinic-dash__stat">
        <span class="adm-stat__icon adm-tone-amber"><i class="fas fa-calendar-alt"></i></span>
        <span>
          <span class="adm-stat__label">Upcoming</span>
          <span class="adm-stat__value">
            <span v-if="!statsLoaded" class="adm-skeleton clinic-dash__skeleton"></span>
            <template v-else>{{ totals.upcoming }}</template>
          </span>
        </span>
      </div>
    </div>

    <div class="clinic-dash__grid">
      <section>
        <div class="clinic-dash__section-head">
          <h2 class="adm-card__title">Doctors</h2>
          <span class="adm-muted">Open a doctor to see and manage their appointments.</span>
        </div>

        <div v-if="loading" class="clinic-dash__doctors">
          <div v-for="n in 3" :key="n" class="adm-card clinic-doc">
            <div class="adm-person"><span class="adm-avatar adm-avatar--lg"></span><div class="adm-skeleton" style="width: 60%"></div></div>
          </div>
        </div>
        <div v-else-if="!doctors.length" class="adm-card adm-empty">
          <i class="fas fa-user-md"></i>
          <strong>No doctors assigned yet</strong>
          Ask your YourDoc administrator to assign doctors to this clinic login.
        </div>
        <div v-else class="clinic-dash__doctors">
          <article v-for="doctor in doctors" :key="key(doctor)" class="adm-card clinic-doc">
            <div class="adm-person clinic-doc__head">
              <span class="adm-avatar adm-avatar--lg">
                <img v-if="images[doctor.doctorid]" :src="images[doctor.doctorid]" :alt="doctor.doctorname" />
                <template v-else>{{ initials(doctor.doctorname) }}</template>
              </span>
              <span class="clinic-doc__name">
                <span class="adm-person__name">Dr. {{ doctor.doctorname }}</span>
                <span class="adm-person__meta">{{ doctor.specialization || "General" }}</span>
                <span class="adm-person__meta clinic-doc__quals">
                  {{ [doctor.qualifications, doctor.experience ? `${doctor.experience} yrs` : ""].filter(Boolean).join(" · ") }}
                </span>
              </span>
            </div>

            <div class="clinic-doc__counts">
              <div>
                <span class="clinic-doc__count">{{ countFor(doctor, "todayAppointments") }}</span>
                <span class="adm-muted">Today</span>
              </div>
              <div>
                <span class="clinic-doc__count">{{ countFor(doctor, "upcomingAppointment") }}</span>
                <span class="adm-muted">Upcoming</span>
              </div>
              <div v-if="multipleClinics">
                <span class="clinic-doc__clinic" :title="doctor.clinicname">{{ doctor.clinicname }}</span>
                <span class="adm-muted">Clinic</span>
              </div>
            </div>

            <div class="clinic-doc__actions">
              <button type="button" class="adm-btn adm-btn--primary" @click="open(doctor, '/clinic-doctor-dashboard')">
                <i class="fas fa-calendar-check"></i> Appointments
              </button>
              <button type="button" class="adm-btn" @click="open(doctor, '/add-clinic-appointment')">
                <i class="fas fa-plus"></i> Book
              </button>
            </div>
          </article>
        </div>
      </section>

      <section class="adm-card clinic-dash__today">
        <div class="adm-card__header">
          <div>
            <h2 class="adm-card__title">Today's appointments</h2>
            <p class="adm-card__hint">Across all your doctors.</p>
          </div>
        </div>
        <div v-if="todayLoading">
          <div v-for="n in 3" :key="n" class="adm-list-row"><div class="adm-skeleton" style="width: 70%"></div></div>
        </div>
        <div v-else-if="!todayAppointments.length" class="adm-empty clinic-dash__empty">
          <i class="fas fa-mug-hot"></i>
          <strong>Nothing booked today</strong>
        </div>
        <div v-else class="clinic-dash__list">
          <div v-for="appt in todayAppointments" :key="appt.slno" class="adm-list-row">
            <span class="clinic-dash__time">{{ appt.appointmenttime }}</span>
            <div class="clinic-dash__who">
              <span class="adm-person__name">{{ appt.patientname }}</span>
              <span class="adm-person__meta">Dr. {{ appt.doctorname }}</span>
            </div>
            <span class="adm-badge" :class="statusClass(appt.appointmentstatus)">{{ appt.appointmentstatus }}</span>
          </div>
        </div>
      </section>
    </div>
  </clinic-layout>
</template>

<script>
import axios from "axios";
import ClinicLayout from "./components/ClinicLayout.vue";
import { initials } from "../admin/admin-utils";

function ddmmyyyy(date) {
  const pad = n => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}

export default {
  name: "clinic-dashboard",
  components: { ClinicLayout },
  data() {
    return {
      doctors: [],
      loading: true,
      stats: {},
      statsLoaded: false,
      images: {},
      appointments: [],
      todayLoading: true,
    };
  },
  computed: {
    todayLabel() {
      return new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    },
    multipleClinics() {
      return new Set(this.doctors.map(d => d.clinicid)).size > 1;
    },
    totals() {
      return Object.values(this.stats).reduce(
        (sum, s) => ({ today: sum.today + (s.todayAppointments || 0), upcoming: sum.upcoming + (s.upcomingAppointment || 0) }),
        { today: 0, upcoming: 0 });
    },
    todayAppointments() {
      const today = ddmmyyyy(new Date());
      return this.appointments
        .filter(a => a.appointmentdate === today && a.appointmentstatus !== "Cancelled")
        .sort((a, b) => String(a.appointmenttime).localeCompare(String(b.appointmenttime)));
    },
  },
  methods: {
    initials,
    key(doctor) {
      return `${doctor.clinicid}-${doctor.doctorid}`;
    },
    countFor(doctor, field) {
      const stats = this.stats[this.key(doctor)];
      return stats ? stats[field] || 0 : "–";
    },
    statusClass(status) {
      return { Confirmed: "adm-badge--info", Completed: "adm-badge--active", Cancelled: "adm-badge--rejected" }[status] || "";
    },
    open(doctor, path) {
      this.$store.dispatch("storeClinicDetails", {
        clinicId: doctor.clinicid,
        clinicname: doctor.clinicname,
        doctorId: doctor.doctorid,
        doctorImage: this.images[doctor.doctorid] || "",
        doctorName: doctor.doctorname,
        specialization: doctor.specialization || "",
      });
      this.$router.push(path);
    },
    async fetchDoctors() {
      const clinicuserid = sessionStorage.getItem("userdbid");
      try {
        const { data } = await axios.get(`/clinic/${clinicuserid}`);
        this.doctors = data.doctors || [];
      } catch (error) {
        this.doctors = [];
      } finally {
        this.loading = false;
      }
      this.fetchStats();
      this.doctors.forEach(this.fetchImage);
    },
    async fetchStats() {
      await Promise.all(this.doctors.map(async doctor => {
        try {
          const { data } = await axios.get(`/clinic/${doctor.doctorid}/doctorstats/${encodeURIComponent(doctor.clinicid)}`);
          this.stats = { ...this.stats, [this.key(doctor)]: data };
        } catch (error) {
          // the card shows "–"
        }
      }));
      this.statsLoaded = true;
    },
    async fetchImage(doctor) {
      if (!doctor.doctorimage || this.images[doctor.doctorid] !== undefined) return;
      this.images[doctor.doctorid] = "";
      try {
        const { data } = await axios.get(`doctors/doctorimg/${doctor.doctorid}/download`, { responseType: "arraybuffer" });
        this.images[doctor.doctorid] = URL.createObjectURL(new Blob([data]));
      } catch (error) {
        // initials are shown instead
      }
    },
    async fetchAppointments() {
      try {
        const { data } = await axios.get(`/clinic/${sessionStorage.getItem("userdbid")}/appointments`);
        this.appointments = (data && data.appointmentlist) || [];
      } catch (error) {
        this.appointments = [];
      } finally {
        this.todayLoading = false;
      }
    },
  },
  created() {
    this.fetchDoctors();
    this.fetchAppointments();
  },
};
</script>

<style scoped>
.clinic-dash__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 20px;
  align-items: start;
}

.clinic-dash__section-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-bottom: 12px;
}

.clinic-dash__section-head .adm-muted {
  font-size: 13px;
}

.clinic-dash__doctors {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.clinic-dash__stat {
  cursor: default;
}

.clinic-dash__skeleton {
  display: block;
  width: 48px;
  height: 26px;
}

.clinic-doc {
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.clinic-doc:hover {
  border-color: #b2ddff;
  box-shadow: 0 4px 8px -2px rgba(16, 24, 40, 0.08);
}

.clinic-doc__head {
  align-items: flex-start;
}

.clinic-doc__name {
  min-width: 0;
}

.clinic-doc__quals {
  font-size: 12px;
  margin-top: 2px;
}

.clinic-doc__counts {
  display: flex;
  gap: 8px;
  border-top: 1px solid var(--adm-line-2);
  border-bottom: 1px solid var(--adm-line-2);
  padding: 12px 0;
}

.clinic-doc__counts > div {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.clinic-doc__counts > div + div {
  border-left: 1px solid var(--adm-line-2);
}

.clinic-doc__counts span {
  display: block;
  font-size: 12px;
}

.clinic-doc__count {
  color: var(--adm-ink);
  font-size: 20px !important;
  font-weight: 700;
  line-height: 1.3;
}

.clinic-doc__clinic {
  color: var(--adm-ink);
  font-weight: 600;
  font-size: 13px !important;
  line-height: 1.9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clinic-doc__actions {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}

.clinic-dash__today {
  position: sticky;
  top: 90px;
}

.clinic-dash__list {
  max-height: 460px;
  overflow-y: auto;
}

.clinic-dash__time {
  font-weight: 700;
  color: var(--adm-primary-600);
  background: var(--adm-primary-50);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 13px;
  min-width: 56px;
  text-align: center;
}

.clinic-dash__who {
  flex: 1;
  min-width: 0;
}

.clinic-dash__empty {
  padding: 32px 20px;
}

@media (max-width: 1199.98px) {
  .clinic-dash__grid {
    grid-template-columns: 1fr;
  }

  .clinic-dash__today {
    position: static;
  }
}
</style>
